/**
 * **Card Master Carl**: the card game's AI persona (trading card game, PRD #493).
 * He originally stood on the bank's south lawn as a vendor NPC (Slice 4), but the
 * card economy now runs entirely through the Card Binder gump - packs are bought
 * and opened from its Packs tab, and a free practice match starts from the Binder
 * anywhere in the world. Carl therefore no longer exists as a world entity: he
 * survives only as the **named practice opponent** the learner faces (PRD #493
 * Slice 6) - his name, his battle-gump portrait look, and the sentinel id the
 * practice match seats him under.
 */
import { bodies } from './content';
import { assembleEquipment, type RequestedLayer } from './equipAssembly';
import { DEFAULT_SKIN_HUE } from './skinTones';
import type { EntityId, RenderableComponent } from './entity';

/** Carl's display name - the practice match's on-screen opponent. */
export const CARD_MASTER_NAME = 'Card Master Carl';

/** The rich violet his robe + feathered hat are hued (a card-dealer's flair). */
export const CARD_MASTER_ROBE_HUE = 1157;

/**
 * The practice match's foe seat id: Carl has **no world entity**, so the server
 * seats him under this never-allocated sentinel. Every lookup against it resolves
 * to nothing by construction (no world entity, no connection, no registry key);
 * the client keys his canned {@link cardMasterRenderable} portrait off it.
 */
export const PRACTICE_OPPONENT_ID = -1 as EntityId;

/**
 * Carl's signature look (the retired vendor NPC's exact kit: long hair, goatee,
 * plain robe, and feathered hat in his violet on the male player body), kept so
 * the practice battle gump can still render his hero portrait through the shared
 * player-art path even though no world entity carries it.
 */
export function cardMasterRenderable(): RenderableComponent {
  const body = bodies.get('male').body;
  const requested: RequestedLayer[] = [
    { id: 'hair.long', hue: CARD_MASTER_ROBE_HUE },
    { id: 'beard.goatee' },
    { id: 'robe.plain', hue: CARD_MASTER_ROBE_HUE },
    { id: 'hat.feathered', hue: CARD_MASTER_ROBE_HUE },
  ];
  return {
    bodyHue: DEFAULT_SKIN_HUE,
    body,
    name: CARD_MASTER_NAME,
    equipment: assembleEquipment(requested, body),
  };
}
